import { Stack } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { WebViewEvents } from '../../common/enums/webViewEvents';
import HistoryIcon from '../../assets/icons/historyIcon.svg?react';
import {
  isReactNative,
  sendEventToReactNative,
} from '../../common/utils/reactNativeHelpers';
import { Discount } from '../../components/Discount/Discount';
import { Loading } from '../../components/common/Loading/Loading';
import { OfferSlider } from '../../components/features/OfferSlider/OfferSlider';
import { UserBalance } from '../../components/common/UserBalance/UserBalamce';
import { ROUTES } from '../../constants/routes';
import { useSessionStatus } from '../../hooks/useSessionStatus';
import { useActiveSessions } from '../../hooks/useActiveSessions';
import { heldForCharging } from '../../utils/activeSessions';
import { useUser } from '../../hooks/useUser';
import { useUserStatus } from '../../hooks/useUserStatus';
import { localStorageService } from '../../services/localStorageService/localStorageService';
import { PhoneNotConfirmed } from '../../components/forms/PhoneNotConfirmed/PhoneNotConfirmed';
import { HistoryButton } from '../../components/common/Button/HistoryButton/HistoryButton';
import { BiometricDrawer } from './components/BiometricDrawer/BiometricDrawer';
import { AuthService } from '../../services/auth/AuthService';
import { useTranslation } from 'react-i18next';

const authService = new AuthService();

export const Cabinet = () => {
  const [loading, setLoading] = useState(true);
  const [biometricDrawerOpen, setBiometricDrawerOpen] =
    useState<boolean>(false);

  const { t } = useTranslation();
  const { user } = useUser();
  const userId = user?.id || localStorageService().getUserId();
  const navigate = useNavigate();

  const {
    isUserPhoneConfirmed,
    checkUserStatus,
    useBiometric,
    hasShownBiometricDrawer,
    setHasShownBiometricDrawer,
  } = useUserStatus();

  // Called for the loading gate it releases; which session is live is read
  // from useActiveSessions, which reports all of them.
  useSessionStatus({
    setLoading,
    isUserPhoneConfirmed,
  });
  const { sessions: activeSessions } = useActiveSessions();
  const { userBalance } = useUser();

  const onCloseBiometricDrawer = () => {
    setBiometricDrawerOpen(false);
    setHasShownBiometricDrawer(true);
  };

  const onButtonClick = async (value: boolean) => {
    onCloseBiometricDrawer();
    if (value) {
      sendEventToReactNative(WebViewEvents.ASK_BIOMETRIC_AUTH);
    }
    await authService.updateUserBiometricStatus(value);
  };

  useEffect(() => {
    const accessToken = localStorageService().getAccessToken();
    if (userId && userId !== 'undefined' && accessToken) {
      sendEventToReactNative(WebViewEvents.SUCCESS_AUTH);
    }
  }, [userId]);

  useEffect(() => {
    if (useBiometric === null && !hasShownBiometricDrawer) {
      setBiometricDrawerOpen(true);
    } else {
      setBiometricDrawerOpen(false);
    }
  }, [useBiometric, hasShownBiometricDrawer]);

  // Home stays Home during charging — reaching the live session is done via the
  // dedicated charging tab in the bottom navigation, not by redirecting here.
  // The estimated cost is deducted from the wallet upfront on charging start, so
  // surface that reserved ("held") amount alongside the available balance. It
  // covers every charge in flight, not just one of them.
  const held = heldForCharging(activeSessions, userBalance);

  if (isUserPhoneConfirmed === false && loading) {
    return <PhoneNotConfirmed onStatusUpdate={checkUserStatus} />;
  }

  if (loading) return <Loading />;

  return (
    <Stack
      alignItems={'center'}
      sx={{
        gap: { xs: '25px' },
        padding: '0 22px 0',
        marginTop: '30px',
        maxWidth: '400px',
        height: '100%',
      }}
    >
      <UserBalance amount={userBalance} held={held} />
      <Stack direction="row" gap={2}>
        <HistoryButton
          text={t('cabinetPage.historyButtonText')}
          icon={<HistoryIcon width={20} />}
          onClick={() => navigate(`${ROUTES.CAB_SESSIONS}`)}
        />
      </Stack>
      <Discount />
      <OfferSlider />
      {isReactNative ? (
        <BiometricDrawer
          open={biometricDrawerOpen}
          onClose={onCloseBiometricDrawer}
          onButtonClick={onButtonClick}
        />
      ) : null}
    </Stack>
  );
};
